import { Button } from "@/components/ui/button";
import { useCheckout } from "@/hooks/billing/useCheckout";
import {
  useSubscription,
  type BillingOrgRef,
} from "@/hooks/billing/useSubscription";
import { PLAN_RESOURCE_SUMMARY, PLANS } from "../constants";

export function CurrentPlan({ org }: { org?: BillingOrgRef }) {
  const { handleManageBilling } = useCheckout();
  const { data } = useSubscription(org?.id);
  const billingCycle = data?.billingCycle ?? null;
  const currentPlan = data?.currentPlan ?? "free";
  const canceled = data?.canceled ?? false;

  const activePlan = PLANS.find((plan) => plan.type === currentPlan);
  const cycle = billingCycle ?? "monthly";

  if (!activePlan) return null;

  const monthly =
    cycle === "annual"
      ? Math.round(activePlan.yearlyPrice / 12)
      : activePlan.monthlyPrice;
  const billingLabel =
    cycle === "annual" ? "billed annually" : "billed monthly";

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Current plan
        </span>
        <h2 className="text-lg font-medium">Your plan</h2>
        <p className="text-sm text-muted-foreground">
          {activePlan.description}.
        </p>
      </div>

      <div className="space-y-4 rounded-xl border-[0.5px] border-border bg-card p-5">
        <div className="space-y-3">
          <div className="flex items-baseline justify-between gap-6">
            <div className="text-base font-medium">{activePlan.name}</div>
            <div>
              <span className="text-3xl font-medium tabular-nums text-foreground">
                ${monthly}
              </span>
              <span className="text-sm text-muted-foreground"> /mo</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {billingLabel} · cancel anytime
          </p>
        </div>

        <div className="border-t border-border" />

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {PLAN_RESOURCE_SUMMARY[
            activePlan.type as keyof typeof PLAN_RESOURCE_SUMMARY
          ]?.map((row) => (
            <div key={row.key} className="space-y-0.5">
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {row.label}
              </dt>
              <dd className="text-foreground tabular-nums">{row.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {!canceled && (
        <Button
          variant="outline"
          onClick={() => handleManageBilling(org?.slug)}
          className="w-full"
        >
          Manage billing
        </Button>
      )}
    </div>
  );
}
